/**
 * Fase 4 — Los cheques.
 *
 * Un cheque no es plata todavía: es una promesa con fecha. Por eso lo primero
 * que se ve no es la lista completa sino LOS QUE VENCEN PRONTO, que es lo que
 * hay que ir a depositar.
 *
 * Los pasos posibles de cada cheque los decide el servidor: acá se ofrecen
 * los que él dice, para que la pantalla no pueda ofrecer un camino que
 * después va a rechazar.
 */

import { formatearPesos, formatearFecha } from './formato.js';

const $ = (sel, dentro = document) => dentro.querySelector(sel);

function alerta(tipo, texto) {
  const simbolos = { critico: '!', aviso: '!', bien: '✓' };
  const div = document.createElement('div');
  div.className = `alerta ${tipo}`;
  div.setAttribute('role', tipo === 'critico' ? 'alert' : 'status');
  div.innerHTML = `<span class="simbolo" aria-hidden="true">${simbolos[tipo] ?? '·'}</span><span></span>`;
  div.lastElementChild.textContent = texto;
  return div;
}

/** Los mismos pasos que permite el servidor. Si acá sobrara uno, la pantalla
 *  ofrecería algo que el servidor va a rechazar; si faltara, algo que se
 *  puede hacer quedaría escondido. */
const PASOS = {
  en_cartera:  ['depositado', 'endosado', 'rechazado', 'anulado'],
  depositado:  ['acreditado', 'rechazado', 'en_cartera'],
  acreditado:  [],
  rechazado:   ['en_cartera', 'anulado'],
  endosado:    ['rechazado'],
  anulado:     [],
};

const NOMBRE = {
  en_cartera: 'En cartera', depositado: 'Depositado', acreditado: 'Acreditado',
  rechazado: 'Rechazado', endosado: 'Endosado', anulado: 'Anulado',
};

const CLASE = {
  en_cartera: '', depositado: 'aviso', acreditado: 'bien',
  rechazado: 'critico', endosado: '', anulado: 'critico',
};

export async function pantallaDeCheques(contenedor, { pedir }) {
  contenedor.innerHTML = '';
  const [clientesR, proveedoresR] = await Promise.all([
    pedir('/api/clientes'), pedir('/api/proveedores'),
  ]);
  const clientes = clientesR.ok ? clientesR.cuerpo.clientes : [];
  const proveedores = proveedoresR.ok ? proveedoresR.cuerpo.proveedores : [];

  const marco = document.createElement('div');
  marco.innerHTML = `
    <div class="tarjeta">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap">
        <div>
          <h2 style="font-size:16px;margin:0 0 3px">Cheques</h2>
          <p style="color:var(--texto-suave);font-size:12.5px;margin:0">
            Un cheque no es plata hasta que se acredita. Los de arriba son los que vencen antes.</p>
        </div>
        <div style="display:flex;gap:10px;align-items:flex-end">
          <div class="campo" style="margin:0"><label for="chq-estado">Estado</label>
            <select id="chq-estado">
              <option value="">Todos</option>
              ${Object.entries(NOMBRE).map(([v, t]) =>
                `<option value="${v}" ${v === 'en_cartera' ? 'selected' : ''}>${t}</option>`).join('')}
            </select></div>
          <button type="button" class="boton principal" data-accion="nuevo">Cargar cheque</button>
        </div>
      </div>
      <div data-rol="form" hidden style="margin-top:14px"></div>
      <div data-rol="tabla" style="margin-top:14px"></div>
    </div>`;
  contenedor.appendChild(marco);

  const cajaTabla = $('[data-rol="tabla"]', marco);
  const cajaForm = $('[data-rol="form"]', marco);
  const filtroEstado = $('#chq-estado', marco);

  async function refrescar() {
    const estado = filtroEstado.value;
    const r = await pedir(`/api/cheques${estado ? `?estado=${estado}` : ''}`);
    if (!r.ok) {
      cajaTabla.innerHTML = '';
      cajaTabla.appendChild(alerta('critico', r.cuerpo?.error ?? 'No se pudieron traer los cheques.'));
      return;
    }
    dibujar(r.cuerpo.cheques ?? []);
  }

  function dibujar(filas) {
    if (filas.length === 0) {
      cajaTabla.innerHTML =
        `<p style="color:var(--texto-suave);text-align:center;padding:20px 0;margin:0">
           No hay cheques con ese filtro.</p>`;
      return;
    }
    const hoy = new Date().toISOString().slice(0, 10);
    const total = filas.reduce((s, c) => s + BigInt(c.importe_cents), 0n);

    cajaTabla.innerHTML = `
      <div class="tabla-scroll"><table class="datos">
        <thead><tr>
          <th>Número</th><th>Banco</th><th>De</th><th>Se cobra</th>
          <th class="plata">Importe</th><th>Estado</th><th>Qué se puede hacer</th>
        </tr></thead>
        <tbody>${filas.map((c) => {
          const vencido = c.fecha_cobro <= hoy && c.estado === 'en_cartera';
          const pasos = PASOS[c.estado] ?? [];
          return `<tr data-id="${c.id}">
            <td>${c.numero}${c.tipo === 'echeq' ? '<br><small>e-Cheq</small>' : ''}</td>
            <td>${c.banco ?? '—'}</td>
            <td>${c.cliente_nombre ?? c.librador_nombre ?? '—'}</td>
            <td${vencido ? ' style="color:var(--grave);font-weight:600"' : ''}>${
              formatearFecha(c.fecha_cobro)}${vencido ? '<br><small>ya se puede depositar</small>' : ''}</td>
            <td class="plata">${formatearPesos(c.importe_cents)}</td>
            <td><span class="etiqueta ${CLASE[c.estado] ?? ''}">${NOMBRE[c.estado] ?? c.estado}</span>${
              c.estado === 'rechazado' && c.motivo_rechazo
                ? `<br><small style="color:var(--texto-suave)">${c.motivo_rechazo}</small>` : ''}${
              c.estado === 'endosado' && c.proveedor_nombre
                ? `<br><small style="color:var(--texto-suave)">a ${c.proveedor_nombre}</small>` : ''}</td>
            <td>${pasos.length === 0
              ? '<span style="color:var(--texto-suave);font-size:12.5px">nada: ya terminó su camino</span>'
              : pasos.map((p) =>
                  `<button type="button" class="boton" data-paso="${p}" data-cheque="${c.id}"
                           style="margin:2px 3px 2px 0">${NOMBRE[p]}</button>`).join('')}</td>
          </tr>`;
        }).join('')}</tbody>
        <tfoot><tr><td colspan="4"><b>Total</b></td>
          <td class="plata"><b>${formatearPesos(total.toString())}</b></td>
          <td colspan="2"></td></tr></tfoot>
      </table></div>`;

    for (const b of cajaTabla.querySelectorAll('[data-paso]')) {
      b.addEventListener('click', () => void mover(b.dataset.cheque, b.dataset.paso));
    }
  }

  async function mover(id, paso) {
    const cuerpo = { estado: paso };

    if (paso === 'rechazado') {
      const motivo = prompt('¿Por qué rebotó? (sin fondos, mal endosado, cuenta cerrada…)');
      if (motivo === null || motivo.trim() === '') return;
      cuerpo.motivo_rechazo = motivo.trim();
    }
    if (paso === 'endosado') {
      if (proveedores.length === 0) {
        alert('Para endosar un cheque hace falta tener cargado el proveedor.');
        return;
      }
      const lista = proveedores.map((p, i) => `${i + 1}. ${p.razon_social}`).join('\n');
      const elegido = prompt(`¿A qué proveedor se endosa?\n\n${lista}\n\nEscribí el número:`);
      const i = Number(elegido) - 1;
      if (!proveedores[i]) return;
      cuerpo.endosado_a_proveedor_id = proveedores[i].id;
    }

    const r = await pedir(`/api/cheques/${id}/estado`,
      { method: 'POST', body: JSON.stringify(cuerpo) });
    if (!r.ok) { alert(r.cuerpo?.error ?? 'No se pudo cambiar el estado.'); return; }

    // Si rebotó y pagaba un cobro vivo, hay que decirlo con todas las letras:
    // mientras ese cobro exista, el cliente figura como que pagó.
    if (r.cuerpo?.aviso) {
      const cobros = (r.cuerpo.cobros_a_revisar ?? [])
        .map((c) => `· ${c.cliente_nombre}, ${formatearFecha(c.fecha)}, ${formatearPesos(c.importe_cents)}`)
        .join('\n');
      alert(`${r.cuerpo.aviso}\n\nEl cobro que quedó colgado:\n${cobros}\n\n` +
            'Andá a Cuenta corriente y anulalo para que la deuda vuelva.');
    }
    await refrescar();
  }

  function formulario() {
    cajaForm.hidden = false;
    cajaForm.innerHTML = `
      <div data-rol="errores"></div>
      <form method="post" action="/api/cheques" novalidate
            style="border:1px solid var(--borde);border-radius:8px;padding:14px">
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:0 16px">
          <div class="campo"><label for="ch-numero">Número *</label>
            <input type="text" id="ch-numero" name="numero"></div>
          <div class="campo"><label for="ch-tipo">Tipo</label>
            <select id="ch-tipo" name="tipo">
              <option value="fisico" selected>Cheque de papel</option>
              <option value="echeq">e-Cheq</option>
            </select></div>
          <div class="campo"><label for="ch-banco">Banco</label>
            <input type="text" id="ch-banco" name="banco"></div>
          <div class="campo"><label for="ch-librador">Quién lo firma</label>
            <input type="text" id="ch-librador" name="librador_nombre"></div>
          <div class="campo"><label for="ch-cliente">Recibido de</label>
            <select id="ch-cliente" name="recibido_de_cliente_id">
              <option value="">— ninguno —</option>
              ${clientes.map((c) => `<option value="${c.id}">${c.razon_social}</option>`).join('')}
            </select></div>
          <div class="campo"><label for="ch-importe">Importe *</label>
            <input type="text" id="ch-importe" name="importe" inputmode="decimal"></div>
          <div class="campo"><label for="ch-emision">Fecha de emisión</label>
            <input type="date" id="ch-emision" name="fecha_emision"></div>
          <div class="campo"><label for="ch-cobro">Fecha de cobro *</label>
            <input type="date" id="ch-cobro" name="fecha_cobro">
            <div class="ayuda">Si es diferido, ésta es la que importa.</div></div>
        </div>
        <div style="display:flex;gap:10px;margin-top:12px">
          <button type="submit" class="boton principal">Guardar cheque</button>
          <button type="button" class="boton" data-accion="cancelar">Cancelar</button>
        </div>
      </form>`;

    const form = $('form', cajaForm);
    $('[data-accion="cancelar"]', form).addEventListener('click', () => {
      cajaForm.hidden = true; cajaForm.innerHTML = '';
    });
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errores = $('[data-rol="errores"]', cajaForm);
      errores.innerHTML = '';
      const pesos = String(form.elements['importe'].value).replace(/\./g, '').replace(',', '.').trim();
      const centavos = /^\d+(\.\d{1,2})?$/.test(pesos)
        ? `${pesos.split('.')[0]}${(pesos.split('.')[1] ?? '').padEnd(2, '0')}` : '';
      const r = await pedir('/api/cheques', { method: 'POST', body: JSON.stringify({
        numero: form.elements['numero'].value,
        tipo: form.elements['tipo'].value,
        banco: form.elements['banco'].value,
        librador_nombre: form.elements['librador_nombre'].value,
        recibido_de_cliente_id: form.elements['recibido_de_cliente_id'].value,
        importe_cents: centavos,
        fecha_emision: form.elements['fecha_emision'].value,
        fecha_cobro: form.elements['fecha_cobro'].value,
      }) });
      if (!r.ok) {
        for (const er of r.cuerpo?.errores
             ?? [{ rotulo: '', problema: r.cuerpo?.error ?? 'No se pudo guardar.' }]) {
          errores.appendChild(alerta('critico', er.rotulo ? `${er.rotulo}: ${er.problema}` : er.problema));
        }
        return;
      }
      cajaForm.hidden = true; cajaForm.innerHTML = '';
      await refrescar();
    });
  }

  $('[data-accion="nuevo"]', marco).addEventListener('click', formulario);
  filtroEstado.addEventListener('change', () => { void refrescar(); });
  await refrescar();
}
